import {
	IconArrowDownOutline18,
	IconArrowUpOutline18,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { ReportingTone } from "./theme"
import { ReportingChartFrame } from "./chart-frame"
import { categoricalColor, toneColor } from "./theme"

export interface ReportingRankedItem {
	id: string
	label: string
	value: number
	delta: number
}

interface ReportingRankedListProps {
	title: string
	description?: string
	items: ReportingRankedItem[]
	isLoading?: boolean
	className?: string
	valueSuffix?: string
	tone?: ReportingTone
	colorByIndex?: boolean
}

export const ReportingRankedList = ({
	title,
	description,
	items,
	isLoading,
	className,
	valueSuffix = "",
	tone = "primary",
	colorByIndex = false,
}: ReportingRankedListProps) => {
	const max = Math.max(...items.map((i) => i.value), 1)
	const baseColor = toneColor(tone)

	return (
		<ReportingChartFrame
			title={title}
			description={description}
			isLoading={isLoading}
			isEmpty={items.length === 0}
			className={className}
		>
			<ul className="space-y-3">
				{items.map((item, index) => {
					const widthPct = Math.max(4, (item.value / max) * 100)
					const barColor = colorByIndex ? categoricalColor(index) : baseColor
					return (
						<li key={item.id} className="space-y-1">
							<div className="flex items-center justify-between gap-2 text-sm">
								<span className="truncate text-primary">{item.label}</span>
								<div className="flex shrink-0 items-center gap-2">
									<span className="font-medium text-primary">
										{item.value}
										{valueSuffix}
									</span>
									<span
										className={cn(
											"inline-flex min-w-[2.5rem] items-center justify-end gap-0.5 text-xs",
											item.delta > 0 && "text-green-600 dark:text-green-400",
											item.delta < 0 && "text-red-500",
											item.delta === 0 && "text-tertiary",
										)}
									>
										{item.delta > 0 ? (
											<IconArrowUpOutline18 className="h-3 w-3" />
										) : item.delta < 0 ? (
											<IconArrowDownOutline18 className="h-3 w-3" />
										) : (
											<span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
										)}
										{item.delta === 0
											? "0"
											: `${item.delta > 0 ? "+" : ""}${item.delta}`}
									</span>
								</div>
							</div>
							<div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
								<div
									className="h-full rounded-full"
									style={{
										width: `${widthPct}%`,
										backgroundColor: barColor,
									}}
								/>
							</div>
						</li>
					)
				})}
			</ul>
		</ReportingChartFrame>
	)
}
